import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance";
import { isEmpty } from "@utils/var.check";

/**
 * Response handling for the module (Lesson) assign / submit / approve pre-check APIs.
 * Each helper branches a pre-check response into pass, "other errors", and the Adaptive Section
 * Learning Outcome (LO) error (an Adaptive Section missing an LO), which is shown in a blocking modal.
 *
 * Backend contract (confirmed against the sls-backend implementation; the LO flag does not
 * always live in a `code === "ok"` response):
 *
 * 1. `resourceApi.checkResourceAssignable(type, uuid)`
 *      -> `code` is ALWAYS "ok" (boolean result map).
 *      -> `data.adaptiveSectionLearningOutcomeError: boolean` (sibling of existing `data.isAssignable`).
 *
 * 2. `cgLessonApi.checkIfSubmittable(params)`
 *      -> `code` is ALWAYS "ok"; `data` is a validation map (e.g. `{ resourceTaggingError: true }`).
 *      -> `data.adaptiveSectionLearningOutcomeError: boolean`, set ONLY when no other validation key is present.
 *
 * 3. `moeLessonApi.checkIfSubmittable / checkIfApprovable(params)`
 *      -> `code` becomes "error" as soon as `errorMap` has ANY entry (existing `JsonResult(Map)` behaviour).
 *      -> `errorMap.adaptiveSectionLearningOutcome: string`, set ONLY when no other error exists — so when this key is present it is guaranteed
 *         to be the ONLY key in `errorMap`.
 */

export const ADAPTIVE_SECTION_LEARNING_OUTCOME_ERROR_DATA_KEY =
  "adaptiveSectionLearningOutcomeError";
export const ADAPTIVE_SECTION_LEARNING_OUTCOME_ERROR_MAP_KEY = "adaptiveSectionLearningOutcome";

/** Response shape for `resourceApi.checkResourceAssignable` / `cgLessonApi.checkIfSubmittable`. */
export interface AdaptiveSectionLoDataResponse {
  code?: string;
  data?: {
    isAssignable?: boolean;
    adaptiveSectionLearningOutcomeError?: boolean;
    [key: string]: unknown;
  } | null;
}

/** Response shape for `moeLessonApi.checkIfSubmittable` / `checkIfApprovable`. */
export interface AdaptiveSectionLoErrorMapResponse {
  code?: string;
  errorMap?: Record<string, string> | null;
}

export type AdaptiveSectionLoCheckResponse =
  AdaptiveSectionLoDataResponse | AdaptiveSectionLoErrorMapResponse;

interface ModalLike {
  showMessage: (type: string, props: Record<string, unknown>) => void;
}

type TranslateFn = (key: string) => string;

/**
 * Detect whether a response (from any of the 3 backend shapes above) signals that the
 * resource has an Adaptive Section missing a Learning Outcome.
 * Safe to call with either response shape — only the matching field will ever be truthy.
 */
export function hasAdaptiveSectionMissingLo(response: AdaptiveSectionLoCheckResponse): boolean {
  const dataResponse = response as AdaptiveSectionLoDataResponse;
  const errorMapResponse = response as AdaptiveSectionLoErrorMapResponse;
  return (
    Boolean(dataResponse?.data?.[ADAPTIVE_SECTION_LEARNING_OUTCOME_ERROR_DATA_KEY]) ||
    Boolean(errorMapResponse?.errorMap?.[ADAPTIVE_SECTION_LEARNING_OUTCOME_ERROR_MAP_KEY])
  );
}

/**
 * Show the blocking error modal for an Adaptive Section missing a Learning Outcome;
 * `onDismiss` runs on both the primary button and modal close.
 * Plain function (no `useI18n()/useModal()` inside) so it can be used from Options API
 * (`this.$modal`, `this.$i18n.t`) as well as `<script setup>` (via the wrapper below).
 */
export function showAdaptiveSectionLoModal(
  modal: ModalLike,
  t: TranslateFn,
  onDismiss?: () => void,
): void {
  modal.showMessage("error", {
    title: t("adaptive_section_lo_validation_modal_title"),
    content: t("adaptive_section_lo_validation_modal_message_label"),
    primaryBtnLabel: t("button_ok"),
    use_secondaryButton: false,
    actionPrimary: () => onDismiss?.(),
    actionHidden: () => onDismiss?.(),
  });
}

/**
 * Branch helper for `resourceApi.checkResourceAssignable` call sites.
 * `onNotAssignable` (`data.isAssignable === false`) takes precedence over the LO check.
 * Without `onLoMissing`, the default LO modal is shown.
 */
export function validateResourceAssignable(
  response: AdaptiveSectionLoDataResponse,
  modal: ModalLike,
  t: TranslateFn,
  hooks: {
    onNotAssignable: () => void;
    onLoMissing?: () => void;
    onPass: () => void;
  },
): void {
  if (response.data?.isAssignable === false) {
    hooks.onNotAssignable();
    return;
  }
  if (hasAdaptiveSectionMissingLo(response)) {
    (hooks.onLoMissing ?? (() => showAdaptiveSectionLoModal(modal, t)))();
    return;
  }
  hooks.onPass();
}

/**
 * Branch helper for `cgLessonApi.checkIfSubmittable` call sites (Community Gallery), where the
 * backend always returns `code === "ok"` and errors are represented as a boolean validation map
 * in `data`. Due to backend early-gating, `adaptiveSectionLearningOutcomeError` is only ever set when no
 * other key is present, so checking it FIRST still shows other errors before the LO modal
 * (the LO modal only appears once they are resolved).
 */
export function validateCgSubmitable(
  response: AdaptiveSectionLoDataResponse,
  modal: ModalLike,
  t: TranslateFn,
  hooks: {
    onOtherErrors: () => void;
    onLoMissing?: () => void;
    onPass: () => void;
  },
): void {
  if (hasAdaptiveSectionMissingLo(response)) {
    (hooks.onLoMissing ?? (() => showAdaptiveSectionLoModal(modal, t)))();
    return;
  }
  if (!isEmpty(response.data)) {
    hooks.onOtherErrors();
    return;
  }
  hooks.onPass();
}

/**
 * Branch helper for `moeLessonApi.checkIfSubmittable / checkIfApprovable` call sites (and the
 * `LessonErrorContentSubpage.vue::refreshErrors()` re-check), where the backend flips `code` to
 * "error" as soon as ANY errorMap entry exists. Because of backend early-gating,
 * `adaptiveSectionLearningOutcome` is only ever the SOLE errorMap key, so it's safe to check it
 * right after the "ok" branch and before falling back to the generic error-table flow.
 */
export function validateSubmittableOrApprovableResponse(
  response: AdaptiveSectionLoErrorMapResponse,
  modal: ModalLike,
  t: TranslateFn,
  hooks: {
    onOtherErrors: () => void;
    onLoMissing?: () => void;
    onPass: () => void;
  },
): void {
  if (response.code === "ok") {
    hooks.onPass();
    return;
  }
  if (hasAdaptiveSectionMissingLo(response)) {
    (hooks.onLoMissing ?? (() => showAdaptiveSectionLoModal(modal, t)))();
    return;
  }
  hooks.onOtherErrors();
}

/**
 * `<script setup>` convenience wrapper — thin binding over the plain functions above using
 * `useI18n()`/`useModal()`, so callers don't pass `modal`/`t`. Options API code (mixins) should
 * import the plain functions directly and pass `this.$modal` / `this.$i18n.t` instead.
 */
export function useModuleAssignSubmitValidation() {
  const { t } = useI18n();
  const modal = useModal();

  return {
    hasAdaptiveSectionMissingLo,
    showAdaptiveSectionLoModal: (onDismiss?: () => void) =>
      showAdaptiveSectionLoModal(modal, t, onDismiss),
    validateResourceAssignable: (
      response: AdaptiveSectionLoDataResponse,
      hooks: { onNotAssignable: () => void; onLoMissing?: () => void; onPass: () => void },
    ) => validateResourceAssignable(response, modal, t, hooks),
    validateCgSubmitable: (
      response: AdaptiveSectionLoDataResponse,
      hooks: { onOtherErrors: () => void; onLoMissing?: () => void; onPass: () => void },
    ) => validateCgSubmitable(response, modal, t, hooks),
    validateSubmittableOrApprovableResponse: (
      response: AdaptiveSectionLoErrorMapResponse,
      hooks: { onOtherErrors: () => void; onLoMissing?: () => void; onPass: () => void },
    ) => validateSubmittableOrApprovableResponse(response, modal, t, hooks),
  };
}
